type KlaviyoProduct = Record<string, any>;

function klaviyoClient(): {identify?: Function; track?: Function} | null {
  return window['klaviyo'] || null;
}

function asCategories(product: KlaviyoProduct): string[] {
  const names = product['category_names'] || product['extension_attributes']?.['category_names'];
  return Array.isArray(names) ? names.map(String).filter(Boolean) : [];
}

function asImageUrl(product: KlaviyoProduct): string {
  const image = product['default_image'] || product['default_plp_image'];
  if (image && typeof image === 'object') {
    return String(image.url || image.src || '');
  }

  return typeof image === 'string' ? image : '';
}

function asProductUrl(product: KlaviyoProduct): string {
  if (product['url']) {
    return String(product['url']);
  }

  const urlKey = String(product['url_key'] || '').replace(/^\/+/, '');
  if (urlKey) {
    return `${window.location.origin}/${urlKey}`;
  }

  return window.location.href;
}

function asUnitPrice(product: KlaviyoProduct): number {
  return Number(product['final_price_incl_tax'] ?? product['price_incl_tax'] ?? product['price'] ?? 0) || 0;
}

function asProductId(product: KlaviyoProduct): string {
  return String(product['id'] ?? product['product_id'] ?? product['entity_id'] ?? product['sku'] ?? '');
}

function checkoutUrl(): string {
  const origin = typeof window !== 'undefined' && window.location?.origin
    ? window.location.origin
    : '';

  return `${origin}/checkout`;
}

export function buildIdentifyPayload(email: string): Record<string, string> {
  return {
    $email: email,
    email,
  };
}

export function buildAddedToCartPayload(
  product: KlaviyoProduct,
  qty: number = 1,
  maskedQuoteId: string = '',
): Record<string, unknown> {
  const quantity = Number(qty) || 1;
  const price = asUnitPrice(product);
  const name = String(product['name'] || '');
  const item = {
    ProductID: asProductId(product),
    SKU: String(product['sku'] || ''),
    ProductName: name,
    Quantity: quantity,
    ItemPrice: price,
    RowTotal: Number((price * quantity).toFixed(2)),
    ProductURL: asProductUrl(product),
    ImageURL: asImageUrl(product),
    ProductCategories: asCategories(product),
  };

  const payload: Record<string, unknown> = {
    $value: item.RowTotal,
    AddedItemProductName: name,
    AddedItemProductID: item.ProductID,
    AddedItemSKU: item.SKU,
    AddedItemCategories: item.ProductCategories,
    AddedItemImageURL: item.ImageURL,
    AddedItemURL: item.ProductURL,
    AddedItemPrice: price,
    AddedItemQuantity: quantity,
    ItemNames: [name],
    CheckoutURL: checkoutUrl(),
    Items: [item],
  };

  if (maskedQuoteId) {
    payload.MaskedQuoteId = maskedQuoteId;
  }

  return payload;
}

export function buildViewedProductPayload(product: KlaviyoProduct): Record<string, unknown> {
  return {
    ProductName: product['name'],
    ProductID: asProductId(product),
    SKU: String(product['sku'] || ''),
    Categories: asCategories(product),
    ImageURL: asImageUrl(product),
    URL: asProductUrl(product),
    Brand: product['manufacturer'],
    Price: asUnitPrice(product),
  };
}

async function resolveMaskedQuoteId(): Promise<string> {
  if (window['solarQuoteIdMask']) {
    return String(window['solarQuoteIdMask']);
  }

  try {
    const response = await fetch('/api/checkout/quote-mask', {credentials: 'same-origin'});
    const data = await response.json();
    if (data?.masked_quote_id) {
      window['solarQuoteIdMask'] = data.masked_quote_id;
      return String(data.masked_quote_id);
    }
  } catch {
    // Tracking should never block add-to-cart.
  }

  return '';
}

export class Klaviyo {
  identify(email: string): void {
    const normalized = (email || '').trim();
    const klaviyo = klaviyoClient();
    if (!normalized || !klaviyo?.identify) {
      return;
    }

    klaviyo.identify(buildIdentifyPayload(normalized));
  }

  addProductDetail(product: KlaviyoProduct): void {
    const klaviyo = klaviyoClient();
    if (!klaviyo?.track) {
      return;
    }

    klaviyo.track('Viewed Product', buildViewedProductPayload(product));
  }

  async addToCart(product: KlaviyoProduct, qty: number = 1): Promise<void> {
    const klaviyo = klaviyoClient();
    if (!klaviyo?.track) {
      return;
    }

    const maskedQuoteId = await resolveMaskedQuoteId();
    klaviyo.track('Added to Cart', buildAddedToCartPayload(product, qty, maskedQuoteId));
  }
}

const KlaviyoTracking = new Klaviyo();

export {KlaviyoTracking};
